import React, { useMemo, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Flex,
  Button,
  Text,
  Image,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Collapse,
  Divider,
  useBreakpointValue
} from "@chakra-ui/react";
import { HamburgerIcon, CloseIcon } from "@chakra-ui/icons";
import { FiUser } from "react-icons/fi";
import { useUser } from "../../contexts/UserContext";
import { logoutToLanding } from "../../lib/logout";
import { getPlatform } from "../../lib/platformUtils";

// iOS 만 status bar 만큼 nav 상단 패딩 강제 (기존 py={3}=0.75rem 보존). Android/Web 은 이전 동작 유지.
const TOP_INSET_STYLE = getPlatform() === "ios"
  ? { paddingTop: "calc(max(env(safe-area-inset-top), 47px) + 0.75rem)" }
  : undefined;

const PPLLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useUser();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActivePath = (path) => {
    if (path === "/ppl") {
      // 홈은 정확히 '/ppl' 일 때만 활성화
      return location.pathname === "/ppl";
    }
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const showBroadcasterMenu = userInfo.company === "KT";
  const showAdminMenu = userInfo.role?.includes("Admin");

  const menuItems = useMemo(() => {
    const items = [];
    // 홈
    items.push({ id: "home", label: "홈", path: "/ppl" });
    // 공통: 작품 탐색
    items.push({ id: "explore", label: "작품 검색", path: "/ppl/works" });

    // 회사(KT2) 역할: 작품/품목 관리
    if (showBroadcasterMenu) {
      items.push({ id: "content-management", label: "작품 관리", path: "/ppl/broadcaster/works" });
      items.push({ id: "ppl-management", label: "PPL 품목 관리", path: "/ppl/broadcaster/ppl" });
    }

    // 관리자: 카테고리 관리
    if (showAdminMenu) {
      items.push({ id: "category-management", label: "카테고리 관리", path: "/ppl/admin/categories" });
    }

    return items;
  }, [showBroadcasterMenu, showAdminMenu]);

  const isDesktop = useBreakpointValue({ base: false, md: true });

  return (
    <Flex direction="column" minH="100vh" bg="#f7fafc">
      {/* Header (GNB) */}
      <Box bg="white" borderBottom="1px" borderColor="gray.200" px={4} py={3} position="sticky" top={0} zIndex={1000} style={TOP_INSET_STYLE}>
        <Flex align="center" maxW="1400px" mx="auto" justify="space-between" gap={4}>
          {/* Left: Logo + Desktop Nav */}
          <Flex align="center" gap={6} flex={1}>
            <Flex align="center" gap={2} cursor="pointer" onClick={() => navigate("/ppl")}>
              <Image src="/laeebly-icon.png" alt="Laeebly" w="8" h="8" borderRadius="lg" objectFit="contain" />
              <Text fontSize="xl">PPL 솔루션</Text>
            </Flex>

            {/* Desktop LNB */}
            {userInfo?.email && isDesktop && (
              <Flex as="nav" gap={2}>
                {menuItems.map((item) => (
                  <Button
                    key={item.id}
                    size="sm"
                    variant={isActivePath(item.path) ? "solid" : "ghost"}
                    colorScheme={isActivePath(item.path) ? "brand" : "gray"}
                    onClick={() => navigate(item.path)}
                  >
                    {item.label}
                  </Button>
                ))}
              </Flex>
            )}
          </Flex>

          {/* Right: User area + Mobile toggle */}
          <Flex align="center" gap={2}>
            {userInfo?.email ? (
              <>
                {/* Desktop user dropdown */}
                {isDesktop && (
                  <Menu>
                    <MenuButton as={Button} variant="outline" size="sm" borderRadius="md" leftIcon={<FiUser />} px={3} py={2}>
                      <Text fontSize="sm" fontWeight="medium">{userInfo.name}</Text>
                    </MenuButton>
                    <MenuList>
                      {showBroadcasterMenu && (
                        <>
                          <MenuItem onClick={() => navigate("/ppl/broadcaster/works")}>작품 관리</MenuItem>
                          <MenuItem onClick={() => navigate("/ppl/broadcaster/ppl")}>PPL 품목 관리</MenuItem>
                          <Divider my={1} />
                        </>
                      )}
                      <MenuItem onClick={() => navigate("/edit-profile")}>프로필 수정</MenuItem>
                      <MenuItem onClick={logoutToLanding}>로그아웃</MenuItem>
                    </MenuList>
                  </Menu>
                )}
                {/* Mobile menu button */}
                {!isDesktop && (
                  <IconButton
                    aria-label="모바일 메뉴"
                    variant="ghost"
                    icon={mobileMenuOpen ? <CloseIcon /> : <HamburgerIcon />}
                    onClick={() => setMobileMenuOpen((v) => !v)}
                  />
                )}
              </>
            ) : null}
          </Flex>
        </Flex>

        {/* Mobile menu (LNB) */}
        {!isDesktop && userInfo?.email && (
          <Collapse in={mobileMenuOpen} animateOpacity>
            <Box borderTop="1px" borderColor="gray.200" mt={3} pt={3}>
              <Flex direction="column" gap={2}>
                {menuItems.map((item) => (
                  <Button
                    key={item.id}
                    justifyContent="flex-start"
                    variant={isActivePath(item.path) ? "solid" : "ghost"}
                    colorScheme={isActivePath(item.path) ? "brand" : "gray"}
                    onClick={() => {
                      navigate(item.path);
                      setMobileMenuOpen(false);
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
                {/* Company quick links */}
                {showBroadcasterMenu && (
                  <>
                    <Divider />
                    <Button
                      justifyContent="flex-start"
                      variant="ghost"
                      onClick={() => {
                        navigate("/ppl/broadcaster/works");
                        setMobileMenuOpen(false);
                      }}
                    >
                      작품 관리
                    </Button>
                    <Button
                      justifyContent="flex-start"
                      variant="ghost"
                      onClick={() => {
                        navigate("/ppl/broadcaster/ppl");
                        setMobileMenuOpen(false);
                      }}
                    >
                      PPL 품목 관리
                    </Button>
                  </>
                )}
                <Divider />
                <Button justifyContent="flex-start" variant="ghost" onClick={() => navigate("/edit-profile")}>
                  프로필 수정
                </Button>
                <Button justifyContent="flex-start" colorScheme="red" variant="outline" onClick={logoutToLanding}>
                  로그아웃
                </Button>
              </Flex>
            </Box>
          </Collapse>
        )}
      </Box>

      {/* Main Content */}
      <Box flex={1} p={8} maxW="1400px" w="100%" mx="auto">
        <Outlet />
      </Box>

      {/* Footer */}
      <Box bg="gray.900" color="gray.300" mt="auto">
        <Box maxW="1400px" mx="auto" px={8} py={8}>
          <Flex direction={{ base: "column", md: "row" }} gap={8}>
            <Box flex={1}>
              <Flex align="center" gap={2} mb={4}>
                <Image src="/laeebly-icon.png" alt="Laeebly" w="6" h="6" borderRadius="md" objectFit="contain" />
                <Text>PPL 솔루션</Text>
              </Flex>
              <Text fontSize="sm" color="gray.400">콘텐츠와 상품을 연결하는 PPL 가치평가 플랫폼</Text>
            </Box>

            <Box flex={1}>
              <Text mb={4}>서비스 정보</Text>
              <Flex direction="column" gap={2} fontSize="sm" color="gray.400">
                <Button variant="link" colorScheme="whiteAlpha" justifyContent="flex-start" onClick={() => navigate("/terms-of-service")}>이용약관</Button>
                <Button variant="link" colorScheme="whiteAlpha" justifyContent="flex-start" onClick={() => navigate("/privacy-policy")}>개인정보처리방침</Button>
                <Button variant="link" colorScheme="whiteAlpha" justifyContent="flex-start" onClick={() => navigate("/home")}>레이블리</Button>
              </Flex>
            </Box>

            <Box flex={1}>
              <Text mb={4}>회사 정보</Text>
              <Flex direction="column" gap={2} fontSize="sm" color="gray.400">
                <Text>(주) 루나르트 | 대표 권재의</Text>
                <Text>사업자등록번호: 652-88-00793</Text>
                <Text>주소: 서울특별시 성동구 연무장5가길 25, 8층 804호(성수동2가, 성수역 SKV1 Tower)</Text>
                <Text>이메일: contact@laeebly.com</Text>
              </Flex>
            </Box>
          </Flex>

          <Box borderTop="1px" borderColor="gray.800" mt={8} pt={8} textAlign="center" fontSize="sm" color="gray.500">
            © 2025 PPL 솔루션. All rights reserved.
          </Box>
        </Box>
      </Box>
    </Flex>
  );
};

export default PPLLayout;

